import React, { useState, useEffect } from 'react';
import { sendEmailVerification } from 'firebase/auth';
import { useAuth } from '../../../hooks/auth/useAuth';
import { AuthMode } from './AuthModal';
import LoadingButton from '../LoadingButton';
import styles from './AuthModal.module.css';

interface VerificationContentProps {
  onSwitchMode: (mode: AuthMode) => void;
  onSuccess: () => void;
  resetPreventClose?: () => void;
}

const VerificationContent: React.FC<VerificationContentProps> = ({ onSwitchMode, onSuccess, resetPreventClose }) => {
  const { authUser, isEmailVerified, userEmail } = useAuth();
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [emailSentMessage, setEmailSentMessage] = useState('');
  const [isCheckingVerification, setIsCheckingVerification] = useState(true);
  const [isVerificationComplete, setIsVerificationComplete] = useState(false);

  // Real-time verification checking
  useEffect(() => {
    if (!authUser) return;

    const checkVerification = async () => {
      try {
        await authUser.reload();
        if (authUser.emailVerified) {
          setIsCheckingVerification(false);
          setIsVerificationComplete(true);
          
          // Immediately reset preventClose to allow modal closing
          if (resetPreventClose) {
            resetPreventClose();
          }
          
          // Show success message for 2.5 seconds, then close modal
          setTimeout(() => {
            onSuccess();
          }, 2500);
          return;
        }
      } catch (error) {
        console.error('Error checking verification:', error);
      }
      setIsCheckingVerification(false);
    };

    // Check immediately
    checkVerification();

    // Then check every 3 seconds
    const interval = setInterval(checkVerification, 3000);
    
    return () => clearInterval(interval);
  }, [authUser, onSuccess]);

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleResendEmail = async () => {
    if (!authUser || resendCooldown > 0) return;

    setIsResending(true);
    try {
      await sendEmailVerification(authUser);
      setEmailSentMessage('Verification email sent! Check your inbox.');
      setResendCooldown(60); // 60 second cooldown
    } catch (error) {
      console.error('Error sending verification email:', error);
      setEmailSentMessage('Error sending email. Please try again.');
    } finally {
      setIsResending(false);
    }
  };



  return (
    <div className={styles.formContainer}>
      <div className={styles.mainContent}>
        {/* Header */}
        <div className={styles.authHeader}>
          <h1 className={styles.authTitle}>Verify your email address</h1>
          <p className={styles.authSubtitle}>
            We sent a verification link to <strong>{userEmail}</strong>
          </p>
        </div>

        {/* Why Verify */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            color: 'var(--auth-text-primary)', 
            marginBottom: '0.75rem',
            marginTop: '1rem'
          }}>
            Why verify?
          </h3>
          <ul style={{ 
            listStyle: 'none', 
            padding: 0, 
            margin: 0,
            fontSize: '14px',
            lineHeight: '1.5',
            color: 'var(--auth-text-secondary)'
          }}>
            <li style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'flex-start' }}>
              <span style={{ color: 'var(--c-success)', marginRight: '0.5rem', fontWeight: '600' }}>✓</span>
              Contact experts directly
            </li>
            <li style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'flex-start' }}>
              <span style={{ color: 'var(--c-success)', marginRight: '0.5rem', fontWeight: '600' }}>✓</span>
              Access premium features
            </li>
            <li style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'flex-start' }}>
              <span style={{ color: 'var(--c-success)', marginRight: '0.5rem', fontWeight: '600' }}>✓</span>
              Secure your account
            </li>
          </ul>
        </div>

        {/* Verification Status */}
        {isCheckingVerification && (
          <div style={{ 
            padding: '1rem', 
            background: 'var(--c-bg-purple-very-light)', 
            borderRadius: 'var(--radius-lg)', 
            marginBottom: '1rem',
            textAlign: 'center'
          }}>
            <div style={{ 
              fontSize: '14px', 
              color: 'var(--auth-text-secondary)',
              marginBottom: '0.5rem'
            }}>
              Checking for email verification...
            </div>
            <div style={{ 
              width: '20px', 
              height: '20px', 
              border: '2px solid var(--auth-border)',
              borderTop: '2px solid var(--auth-text-primary)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto'
            }} />
          </div>
        )}

        {/* Email Sent Message */}
        {emailSentMessage && (
          <div className={styles.infoMessage} style={{ marginBottom: '1rem' }}>
            {emailSentMessage}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {isVerificationComplete ? (
            /* Verification Complete Message */
            <div 
              className={styles.primaryButton}
              style={{
                background: 'var(--c-success)',
                borderColor: 'var(--c-success)',
                color: 'white',
                cursor: 'default',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              <span>✓</span> Verification complete
            </div>
          ) : (
            /* Resend Email Button */
            <LoadingButton
              variant="primary"
              onClick={handleResendEmail}
              loading={isResending}
              loadingText="Sending..."
              disabled={resendCooldown > 0}
              className={styles.primaryButton}
            >
              {resendCooldown > 0 
                ? `Resend email in ${resendCooldown}s` 
                : 'Resend verification email'
              }
            </LoadingButton>
          )}
        </div>

        {/* Help Text */}
        <div style={{ 
          marginTop: '1.5rem', 
          fontSize: '12px', 
          color: 'var(--auth-text-muted)',
          textAlign: 'center',
          lineHeight: '1.4'
        }}>
          Check your spam folder if you don't see the email.
        </div>
      </div>

      {/* Terms - Pushed to Bottom */}
      <div className={styles.termsText}>
        Need help? <a href="/support" target="_blank" rel="noopener">Contact support</a>
      </div>
    </div>
  );
};

export default VerificationContent;